import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, Search, UserPlus, X } from "lucide-react";
import { api } from "../console/api";
import MoisCalendrier, { moisDe } from "./MoisCalendrier";
import { Pastilles, Titre } from "./ModifierCoursPrive";
import { franc } from "../lib/reglesPrives";

/* « Ajouter un cours » d'un cours privé, par l'école (retour de l'école, 03.11.2026 : « des gens
   ne passent pas par le site, ils en parlent ») — la console (fenêtre) et l'app école (feuille).
   Dans l'ordre :
   · Qui ? — un élève du CRM (chercher un nom, une adresse), sinon « Nouvelle personne » : l'élève,
     son parent si c'est un enfant, l'e-mail et le téléphone. La personne nouvelle reçoit sa fiche
     élève dans le CRM (Jen, 03.11.2026 : « c'est un élève ! »).
   · La discipline, le prof, la durée (le prix du barème, modifiable).
   · Quand ? — les heures libres du prof d'abord ; « Autre date » pour une autre heure ou un cours
     déjà donné (avant l'application, Calendly) : une date passée, aucun e-mail.
   · « Prévenir la famille par e-mail » (un cours à venir ; le prof l'est toujours).
   Après « Ajouter », « Encore un cours pour … » garde l'élève, le prof et la discipline : pour
   reprendre une série de cours passés d'affilée. `useAjout` garde l'état et parle au serveur ;
   `ContenuAjout` l'affiche (les deux écrans posent leurs boutons). */

const heureFr = (h) => { const [a, b] = String(h || "").split(":"); return `${Number(a)}h${b && b !== "00" ? b : ""}`; };
const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const jourLong = (iso) => { const d = new Date(`${iso}T12:00:00`); return `${JOURS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]}`; };
const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
const enMin = (h) => { const [a, b] = String(h || "0:0").split(":").map(Number); return a * 60 + (b || 0); };
const plus = (h, m) => { const t = enMin(h) + Number(m || 0); return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };
const aujourdhui = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Zurich" });
const VIDE = { eleve_prenom: "", eleve_nom: "", enfant: false, prenom: "", nom: "", email: "", telephone: "" };

export function useAjout(onFait, depart = null) {
  // Qui : un élève du CRM, ou une nouvelle personne.
  const [eleve, setEleve] = useState(null);
  const [nouveau, setNouveau] = useState(false);
  const [perso, setPerso] = useState(VIDE);
  const [q, setQ] = useState("");
  const [trouves, setTrouves] = useState([]);
  // Le cours.
  const [disc, setDisc] = useState("");
  // `depart` : le moment touché dans le calendrier (le prof, le jour, l'heure), déjà rempli.
  const [prof, setProf] = useState(depart?.prof_id || "");
  const [minutes, setMinutes] = useState(0);
  const [jour, setJour] = useState(depart?.date || "");
  const [debut, setDebut] = useState(depart?.debut || "");
  const [mode, setMode] = useState(depart ? "autre" : "libres");
  const [mois, setMois] = useState(moisDe(depart?.date || aujourdhui()));
  const [prixLibre, setPrixLibre] = useState("");
  const [objectif, setObjectif] = useState("");
  const [prevenir, setPrevenir] = useState(true);
  const [sansPause, setSansPause] = useState(false);
  useEffect(() => { setSansPause(false); }, [disc, prof, minutes, jour, debut]);
  const [d, setD] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [fait, setFait] = useState(null);           // le dernier cours ajouté (« C'est ajouté … »)

  // Les choix, les heures libres et la vérification du moment : seule la DERNIÈRE réponse compte.
  const tour = useRef(0);
  useEffect(() => {
    const n = ++tour.current;
    setCharge(true);
    const p = new URLSearchParams({ discipline: disc, minutes: String(minutes || 0), prof, jour: jour || "", debut: debut || "" });
    const t = setTimeout(() => {
      api(`/reservations-privees/ajout?${p.toString()}`)
        .then((x) => {
          if (n !== tour.current) return;
          setD(x); setCharge(false);
          // Le premier chargement (ou un choix devenu impossible) : ce que le serveur a retenu.
          if (x.choix) {
            if (x.choix.discipline !== disc) setDisc(x.choix.discipline);
            if (x.choix.prof !== prof) setProf(x.choix.prof);
            if (x.choix.minutes !== minutes) setMinutes(x.choix.minutes);
          }
        })
        .catch((e) => { if (n === tour.current) { setErreur(e.message); setCharge(false); } });
    }, d ? 250 : 0);
    return () => clearTimeout(t);
  }, [disc, prof, minutes, jour, debut]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chercher un élève du CRM (nom, prénom, parent, adresse).
  useEffect(() => {
    if (q.trim().length < 2) { setTrouves([]); return undefined; }
    const t = setTimeout(() => {
      api(`/eleves?q=${encodeURIComponent(q.trim())}&limite=6`).then((x) => setTrouves(x.resultats || [])).catch(() => setTrouves([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const disciplines = d?.disciplines || [];
  const discipline = disciplines.find((x) => x.id === disc);
  const profs = discipline?.profs || [];
  const durees = discipline?.durees || [];
  const prixBareme = durees.find((x) => x.minutes === minutes)?.prix;
  const prix = prixLibre !== "" && !Number.isNaN(Number(prixLibre)) ? Number(prixLibre) : prixBareme;
  const nomProf = profs.find((p) => p.id === prof)?.prenom || "";
  const choisirDiscipline = (id) => {
    const x = disciplines.find((y) => y.id === id);
    if (!x) return;
    setErreur(""); setDisc(id); setPrixLibre("");
    if (!x.profs.some((p) => p.id === prof)) setProf(x.profs[0]?.id || "");
    if (!x.durees.some((y) => y.minutes === minutes)) setMinutes(x.durees[0]?.minutes || 0);
  };
  const parJour = useMemo(() => {
    const m = {};
    (d?.creneaux || []).forEach((c) => { (m[c.date] ||= []).push(c); });
    return m;
  }, [d]);
  const joursCal = useMemo(() => Object.fromEntries(Object.keys(parJour).map((j) => [j, { points: [] }])), [parJour]);
  const v = d?.verification;
  const passe = !!v?.passe;
  const nomEleve = eleve ? `${eleve.prenom || ""} ${eleve.nom || ""}`.trim() : `${perso.eleve_prenom} ${perso.eleve_nom}`.trim();
  const quiPret = eleve ? true : (!!perso.eleve_prenom.trim() && !!perso.eleve_nom.trim() && /\S+@\S+\.\S+/.test(perso.email.trim()));
  const pret = quiPret && !!disc && !!prof && !!minutes && !!jour && !!debut && !!v?.possible
    && (!v?.avertissement || sansPause) && !charge && !envoi;

  const ajouter = async () => {
    if (!pret) return;
    setEnvoi(true); setErreur("");
    const corps = {
      discipline: disc, minutes, prof_id: prof, date: jour, debut, objectif, prevenir: prevenir && !passe, sans_pause: sansPause,
      ...(prixLibre !== "" && prix !== prixBareme ? { montant: Number(prix) } : {}),
      ...(eleve ? { eleve_id: eleve.id } : {
        pour: perso.enfant ? "enfant" : "moi", eleve_prenom: perso.eleve_prenom, eleve_nom: perso.eleve_nom,
        prenom: perso.prenom, nom: perso.nom, email: perso.email, telephone: perso.telephone,
      }),
    };
    try {
      const x = await api("/reservations-privees/ajouter", { method: "POST", body: JSON.stringify(corps) });
      setFait({ message: x.message, eleve: nomEleve });
      setEnvoi(false);
      onFait(x);
    } catch (e) {
      setErreur(e.message); setEnvoi(false);
    }
  };
  // « Encore un cours pour … » : le même élève, le même prof, la même discipline ; un autre moment.
  const encore = () => { setFait(null); setJour(""); setDebut(""); setObjectif(""); setErreur(""); };
  const choisirEleve = (e) => { setEleve(e); setNouveau(false); setQ(""); setTrouves([]); setErreur(""); };
  const changerEleve = () => { setEleve(null); setNouveau(false); setPerso(VIDE); setQ(""); };
  return {
    d, charge, erreur, envoi, pret, fait, passe, v, eleve, nouveau, perso, q, trouves, disc, prof, minutes, jour, debut, mode,
    mois, prix, prixBareme, prixLibre, objectif, prevenir, sansPause, disciplines, profs, durees, parJour, joursCal, nomProf, nomEleve,
    setQ, setNouveau: (x) => { setNouveau(x); setEleve(null); }, setPerso: (champs) => setPerso((p) => ({ ...p, ...champs })),
    choisirEleve, changerEleve, choisirDiscipline, setProf: (p) => { setErreur(""); setProf(p); },
    setMinutes: (m) => { setErreur(""); setMinutes(m); setPrixLibre(""); }, setMode, setMois, setPrixLibre, setObjectif,
    setPrevenir, setSansPause, choisirJour: (j) => { setErreur(""); setJour(j); setDebut(""); },
    choisirHeure: (h) => { setErreur(""); setDebut(h); }, setJour, setDebut, ajouter, encore,
  };
}

const Champ = ({ label, valeur, onChange, type = "text", mobile, testid, placeholder }) => (
  <label className="block min-w-0">
    <span className="block text-xs text-[var(--muted)] mb-1">{label}</span>
    <input type={type} className={mobile ? "input-field text-[15px] w-full" : "input-field text-sm w-full"} value={valeur}
      placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </label>
);

export function ContenuAjout({ m, mobile = false }) {
  const { d } = m;
  if (!d) return <p className="text-sm text-[var(--muted)] py-6">{m.erreur || "Chargement…"}</p>;
  const champ = mobile ? "input-field text-[15px] w-full" : "input-field text-sm w-full";
  const heures = m.parJour[m.jour] || [];
  const discLabel = m.disciplines.find((x) => x.id === m.disc)?.label || "";
  const minJour = Object.keys(m.parJour)[0];
  const maxJour = Object.keys(m.parJour).slice(-1)[0];
  const p = m.perso;
  return (
    <div className={mobile ? "space-y-4" : "space-y-4 text-sm"} data-testid="ajout-cours">
      {/* 1 · Qui ? */}
      <div>
        <Titre mobile={mobile}>Pour qui ?</Titre>
        {m.eleve ? (
          <div className="rounded-2xl border px-3.5 py-2.5 flex items-center gap-3" style={{ borderColor: "var(--primary)", background: "var(--primary-tint)" }}
            data-testid="ajout-eleve-choisi">
            <Check size={16} className="shrink-0" style={{ color: "#0F766E" }} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{m.nomEleve}</div>
              <div className="text-xs text-[var(--muted)] truncate">
                {[m.eleve.responsable && m.eleve.responsable !== m.nomEleve ? m.eleve.responsable : "", m.eleve.email, m.eleve.telephone].filter(Boolean).join(" · ") || "Pas d'adresse e-mail sur la fiche"}
              </div>
            </div>
            <button type="button" className="text-xs underline text-[var(--muted)]" onClick={m.changerEleve} data-testid="ajout-eleve-changer">Changer</button>
          </div>
        ) : m.nouveau ? (
          <div className="rounded-2xl border p-3.5 space-y-3" style={{ borderColor: "var(--border)" }} data-testid="ajout-nouveau">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Nouvelle personne</span>
              <span className="text-xs text-[var(--muted)]">— sa fiche élève se crée dans le CRM</span>
              <button type="button" className="ml-auto text-[var(--muted)]" onClick={() => m.setNouveau(false)} aria-label="Chercher plutôt un élève"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Champ mobile={mobile} label="Prénom de l'élève" valeur={p.eleve_prenom} onChange={(x) => m.setPerso({ eleve_prenom: x })} testid="ajout-eleve-prenom" />
              <Champ mobile={mobile} label="Nom" valeur={p.eleve_nom} onChange={(x) => m.setPerso({ eleve_nom: x })} testid="ajout-eleve-nom" />
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" className={mobile ? "w-5 h-5" : ""} checked={p.enfant} onChange={(e) => m.setPerso({ enfant: e.target.checked })} data-testid="ajout-enfant" />
              <span>C'est un enfant (on écrit à son parent)</span>
            </label>
            {p.enfant && (
              <div className="grid grid-cols-2 gap-2">
                <Champ mobile={mobile} label="Prénom du parent" valeur={p.prenom} onChange={(x) => m.setPerso({ prenom: x })} testid="ajout-parent-prenom" />
                <Champ mobile={mobile} label="Nom du parent" valeur={p.nom} onChange={(x) => m.setPerso({ nom: x })} testid="ajout-parent-nom" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
              <Champ mobile={mobile} label="E-mail" type="email" valeur={p.email} onChange={(x) => m.setPerso({ email: x })} testid="ajout-email" />
              <Champ mobile={mobile} label="Téléphone" type="tel" valeur={p.telephone} onChange={(x) => m.setPerso({ telephone: x })} testid="ajout-telephone" />
            </div>
          </div>
        ) : (
          <div>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input className={champ} style={{ paddingLeft: 34 }} value={m.q} onChange={(e) => m.setQ(e.target.value)}
                placeholder="Chercher un élève : nom, prénom, e-mail…" data-testid="ajout-chercher" autoFocus={!mobile} />
            </div>
            {m.trouves.length > 0 && (
              <div className="mt-1.5 rounded-2xl border divide-y overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="ajout-resultats">
                {m.trouves.map((e) => (
                  <button key={e.id} type="button" onClick={() => m.choisirEleve(e)} data-testid={`ajout-resultat-${e.id}`}
                    className={`w-full text-left px-3.5 flex items-center gap-3 hover:bg-[var(--primary-tint)] ${mobile ? "min-h-[52px]" : "py-2"}`}>
                    <span className="font-medium truncate">{`${e.prenom || ""} ${e.nom || ""}`.trim()}</span>
                    <span className="text-xs text-[var(--muted)] truncate ml-auto">{[e.responsable, e.email].filter(Boolean).join(" · ")}</span>
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={() => m.setNouveau(true)} data-testid="ajout-nouvelle-personne"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "#0F766E" }}>
              <UserPlus size={15} /> {m.q.trim().length >= 2 && !m.trouves.length ? "Personne trouvée — ajouter une nouvelle personne" : "Nouvelle personne"}
            </button>
          </div>
        )}
      </div>

      {/* 2 · Le cours */}
      <div>
        <Titre mobile={mobile}>Discipline</Titre>
        <Pastilles mobile={mobile} testid="ajout-discipline" valeur={m.disc} onChange={m.choisirDiscipline}
          options={m.disciplines.map((x) => ({ valeur: x.id, label: x.label }))} />
      </div>
      <div className={mobile ? "space-y-4" : "grid md:grid-cols-2 gap-4"}>
        <div>
          <Titre mobile={mobile}>Prof</Titre>
          <Pastilles mobile={mobile} testid="ajout-prof" valeur={m.prof} onChange={m.setProf}
            options={m.profs.map((x) => ({ valeur: x.id, label: x.prenom, couleur: x.couleur }))} />
        </div>
        <div>
          <Titre mobile={mobile}>Durée</Titre>
          <Pastilles mobile={mobile} testid="ajout-duree" valeur={m.minutes} onChange={m.setMinutes}
            options={m.durees.map((x) => ({ valeur: x.minutes, label: `${x.minutes} min`, detail: `CHF ${franc(x.prix)}` }))} />
        </div>
      </div>

      {/* 3 · Quand ? */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Titre mobile={mobile}>Quand ?</Titre>
          <div className="inline-flex rounded-full p-0.5" style={{ background: "#F1F5F9" }} role="tablist">
            {[["libres", `Heures libres de ${m.nomProf || "…"}`], ["autre", "Autre date · cours passé"]].map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={m.mode === k} onClick={() => m.setMode(k)} data-testid={`ajout-mode-${k}`}
                className={`rounded-full font-medium ${mobile ? "px-3 min-h-[34px] text-[13px]" : "px-3 py-1 text-xs"}`}
                style={m.mode === k ? { background: "#fff", boxShadow: "0 1px 3px rgba(15,27,45,0.12)" } : { color: "var(--muted)" }}>{l}</button>
            ))}
          </div>
        </div>
        {m.mode === "libres" ? (
          !Object.keys(m.parJour).length && !m.charge ? (
            <p className="text-[var(--muted)]" data-testid="ajout-aucune-heure">
              Aucune heure libre pour {m.nomProf} ces prochaines semaines. Choisis « Autre date » : l'école décide.
            </p>
          ) : (
            <div className={mobile ? "space-y-3" : "grid md:grid-cols-[300px_1fr] gap-4 items-start"}>
              <MoisCalendrier mois={m.mois} onMois={m.setMois} jours={m.joursCal} choisi={m.jour} onChoisir={m.choisirJour}
                testid="ajout-jours" min={minJour} max={maxJour} aujourdhui={aujourdhui()} />
              <div>
                <div className="font-medium mb-2">{m.jour ? jourLong(m.jour).replace(/^./, (c) => c.toUpperCase()) : "Choisis un jour en vert"}</div>
                <div className="flex flex-wrap gap-2" data-testid="ajout-heures">
                  {heures.map((c) => {
                    const on = c.debut === m.debut;
                    return (
                      <button key={c.debut} type="button" onClick={() => m.choisirHeure(c.debut)} data-testid={`ajout-heure-${c.debut}`} aria-pressed={on}
                        className={`rounded-full border font-semibold tabular-nums ${mobile ? "px-4 min-h-[42px] text-[15px]" : "px-3.5 py-1.5 text-sm"}`}
                        style={on ? { background: "var(--primary)", borderColor: "var(--primary)", color: "#04202A" } : { borderColor: "var(--border)", background: "#fff" }}>
                        {c.debut}
                      </button>
                    );
                  })}
                  {m.jour && !heures.length && !m.charge && <p className="text-[var(--muted)]">Pas d'heure libre ce jour-là : choisis un jour en vert, ou « Autre date ».</p>}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-wrap gap-3 items-end">
            <label className="block"><span className="block text-xs text-[var(--muted)] mb-1">Date</span>
              <input type="date" className={champ} style={{ minWidth: 160 }} value={m.jour} onChange={(e) => m.setJour(e.target.value)} data-testid="ajout-date" /></label>
            <label className="block"><span className="block text-xs text-[var(--muted)] mb-1">Heure de début</span>
              <input type="time" className={champ} style={{ minWidth: 120 }} step={300} value={m.debut} onChange={(e) => m.setDebut(e.target.value)} data-testid="ajout-heure" /></label>
            {m.debut && <span className="text-[var(--muted)] pb-2">→ fin à {heureFr(plus(m.debut, m.minutes))}</span>}
          </div>
        )}
        {/* Ce moment est-il possible ? Le serveur le dit à chaque choix. */}
        {m.jour && m.debut && m.v && !m.charge && (
          <p className="mt-2 flex items-start gap-1.5 rounded-xl px-3 py-2" data-testid="ajout-verif" data-possible={m.v.possible ? "oui" : "non"}
            style={m.v.possible ? (m.passe ? { background: "#F1F5F9", color: "#334155" } : { background: "#F0FDF4", color: "#15803D" }) : { background: "#FEF2F2", color: "#B91C1C" }}>
            {m.v.possible ? <Check size={15} className="shrink-0 mt-0.5" /> : <AlertCircle size={15} className="shrink-0 mt-0.5" />}
            <span>{!m.v.possible ? m.v.message
              : m.passe ? `Cours déjà donné : ${m.v.quand}. Il est noté avec les cours passés (et le salaire du prof s'il en a un) ; aucun e-mail ne part.`
                : m.v.dans_ses_dispos ? `Libre : ${m.v.quand}, dans les disponibilités de ${m.nomProf}.`
                  : `Possible : ${m.v.quand}, hors des disponibilités de ${m.nomProf} — l'école décide.`}</span>
          </p>
        )}
        {m.jour && m.debut && m.v?.possible && m.v.avertissement && !m.charge && (
          <div className="mt-2 rounded-xl px-3 py-2 space-y-2" style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A" }} data-testid="ajout-sans-pause-avertissement">
            <p className="flex items-start gap-1.5"><AlertCircle size={15} className="shrink-0 mt-0.5" /><span>{m.v.avertissement}</span></p>
            <label className={`flex items-start gap-2 font-medium ${mobile ? "text-[15px]" : ""}`}>
              <input type="checkbox" className={mobile ? "mt-0.5 w-5 h-5 shrink-0" : "mt-0.5"} checked={m.sansPause} onChange={(e) => m.setSansPause(e.target.checked)} data-testid="ajout-sans-pause" />
              <span>Placer sans la pause (je confirme)</span>
            </label>
          </div>
        )}
      </div>

      {/* 4 · Le prix, la note, prévenir */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="block"><span className="block text-xs text-[var(--muted)] mb-1">Prix (CHF)</span>
          <input type="number" min={0} step="0.05" className={champ} style={{ width: 120 }} value={m.prixLibre !== "" ? m.prixLibre : (m.prixBareme ?? "")}
            onChange={(e) => m.setPrixLibre(e.target.value)} data-testid="ajout-prix" /></label>
        {m.prixLibre !== "" && Number(m.prixLibre) !== m.prixBareme && (
          <button type="button" className="text-xs underline text-[var(--muted)] pb-2.5" onClick={() => m.setPrixLibre("")}>prix du barème ({chf(m.prixBareme)})</button>
        )}
      </div>
      <label className="block"><Titre mobile={mobile}>Ce que l'élève aimerait travailler</Titre>
        <textarea className={champ} rows={2} value={m.objectif} onChange={(e) => m.setObjectif(e.target.value)} placeholder="(facultatif)" data-testid="ajout-objectif" /></label>
      {!m.passe && (
        <label className={`flex items-start gap-2.5 ${mobile ? "text-[15px]" : ""}`}>
          <input type="checkbox" className={mobile ? "mt-0.5 w-5 h-5 shrink-0" : "mt-0.5"} checked={m.prevenir} onChange={(e) => m.setPrevenir(e.target.checked)} data-testid="ajout-prevenir" />
          <span>Prévenir la famille par e-mail <span className="text-[var(--muted)]">(confirmation et agenda ; le prof est toujours prévenu)</span></span>
        </label>
      )}

      {m.jour && m.debut && m.v?.possible && (
        <div className="rounded-2xl p-3.5" style={{ background: "var(--primary-tint)" }} data-testid="ajout-recap">
          <div className="font-semibold">{m.nomEleve || "L'élève"} · {discLabel} avec {m.nomProf} · {jourLong(m.jour)} à {heureFr(m.debut)} ({m.minutes} min) · {chf(m.prix)}</div>
          <div className="text-xs text-[var(--muted)] mt-1">
            {m.passe ? "Cours déjà donné : rien ne part." : m.prevenir ? `La famille reçoit la confirmation ; ${m.nomProf} est prévenu·e.` : `La famille n'est pas prévenue ; ${m.nomProf} est prévenu·e.`}
          </div>
        </div>
      )}
      {m.erreur && <p className="rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }} data-testid="ajout-erreur">{m.erreur}</p>}
    </div>
  );
}

/* Après « Ajouter » : ce qui est fait, et « Encore un cours pour … » (une série de cours passés). */
export function AjoutFait({ m, onFermer, mobile = false }) {
  return (
    <div className="space-y-4" data-testid="ajout-fait">
      <p className="rounded-2xl px-4 py-3 flex items-start gap-2" style={{ background: "#F0FDF4", color: "#15803D" }}>
        <Check size={16} className="shrink-0 mt-0.5" /><span>{m.fait.message}</span>
      </p>
      <div className={mobile ? "grid gap-2" : "flex flex-wrap justify-end gap-2"}>
        <button type="button" className={mobile ? "rounded-full min-h-[50px] font-semibold border bg-white" : "btn-ghost"}
          style={mobile ? { borderColor: "var(--border)" } : undefined} onClick={onFermer} data-testid="ajout-terminer">Terminé</button>
        <button type="button" className={mobile ? "rounded-full min-h-[50px] font-semibold" : "btn-primary"}
          style={mobile ? { background: "var(--primary)" } : undefined} onClick={m.encore} data-testid="ajout-encore">
          Encore un cours pour {m.fait.eleve || "cet élève"}
        </button>
      </div>
    </div>
  );
}
